import { useTranslation } from 'react-i18next';
import type { Patient } from '@/api/patients/patientsApi.types';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { IntakeReviewSection } from '../IntakeReviewSection';
import { ProgressSection } from '../ProgressSection';

interface OverviewTabProps {
  patient: Patient;
  hasFirstConsultation: boolean;
  hasSessionNotes: boolean;
}

export function OverviewTab({ patient, hasFirstConsultation, hasSessionNotes }: OverviewTabProps) {
  const { t } = useTranslation('patients');

  return (
    <div className="grid gap-5 items-start">
      <Card>
        <h2 className="text-sm font-bold text-zooindigo mb-4">{t('detail.clinical')}</h2>
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
          <ClinicalField
            label={t('detail.weight')}
            value={patient.weight === null ? null : patient.weight + ' ' + t('form.weightUnit')}
          />
          <NeuteredField neutered={patient.neutered} />
          <ClinicalField
            className="sm:col-span-2 xl:col-span-2"
            label={t('form.mainIssue')}
            value={patient.mainIssue}
          />
          <ClinicalField
            className="sm:col-span-2 xl:col-span-2"
            label={t('form.diagnosis')}
            value={patient.diagnosis}
          />
          <ClinicalField
            className="sm:col-span-2 xl:col-span-2"
            label={t('form.medication')}
            value={patient.medication}
          />
          <ClinicalField
            className="sm:col-span-2 xl:col-span-2"
            label={t('form.pastSurgeries')}
            value={patient.pastSurgeries}
          />
          <ClinicalField
            className="sm:col-span-2 xl:col-span-2"
            label={t('form.allergies')}
            value={patient.allergies}
            isSafety
          />
        </dl>
      </Card>

      {hasFirstConsultation === false ? null : (
        <IntakeReviewSection patientId={patient.id} patient={patient} />
      )}

      {hasSessionNotes === false ? null : <ProgressSection patientId={patient.id} />}
    </div>
  );
}

interface ClinicalFieldProps {
  label: string;
  value: string | null;
  className?: string;
  isSafety?: boolean;
}

function ClinicalField({ label, value, className, isSafety = false }: ClinicalFieldProps) {
  const { t } = useTranslation('patients');
  const isEmpty = value === null || value.trim() === '';
  return (
    <div className={className}>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">{label}</dt>
      <dd
        className={
          'text-sm mt-0.5 break-words ' +
          (isEmpty
            ? 'text-zoomuted italic'
            : isSafety
              ? 'text-zoowarningtext font-semibold whitespace-pre-wrap'
              : 'text-zooindigo whitespace-pre-wrap')
        }
      >
        {isEmpty ? t('detail.notRecorded') : value}
      </dd>
    </div>
  );
}

function NeuteredField({ neutered }: { neutered: boolean | null }) {
  const { t } = useTranslation('patients');
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
        {t('detail.neutered')}
      </dt>
      <dd className="mt-1">
        {neutered === null ? (
          <span className="text-sm text-zoomuted italic">{t('detail.notRecorded')}</span>
        ) : neutered ? (
          <Badge tone="neutral" size="md">
            {t('detail.neutered')}
          </Badge>
        ) : (
          <Badge tone="muted" size="md" outline>
            {t('detail.notNeutered')}
          </Badge>
        )}
      </dd>
    </div>
  );
}
